import config from '../config';
import { getToken, notifySessionExpired } from './auth';

const PUBLIC_ENDPOINTS = new Set(['login']);

function isSessionExpiredMessage(message) {
  return typeof message === 'string' && /session expired/i.test(message);
}

function throwSessionExpired(message) {
  notifySessionExpired(message);
  const error = new Error(message || 'Session expired. Please login again.');
  error.sessionExpired = true;
  throw error;
}

async function readJsonSafe(response) {
  const text = await response.text();
  let data = {};
  if (text) {
    try {
      data = JSON.parse(text);
    } catch (e) {
      data = {};
    }
  }

  const replayHeaders = {};
  const contentType = response.headers && response.headers.get
    ? response.headers.get('content-type')
    : '';
  if (contentType) {
    replayHeaders['Content-Type'] = contentType;
  }

  return {
    data: data,
    replay: new Response(text, {
      status: response.status,
      statusText: response.statusText,
      headers: replayHeaders,
    }),
  };
}

async function interceptSessionExpired(response, endpoint) {
  if (PUBLIC_ENDPOINTS.has(endpoint)) {
    return response;
  }

  const parsed = await readJsonSafe(response);
  const message = parsed.data && parsed.data.message ? String(parsed.data.message) : '';
  if (isSessionExpiredMessage(message)) {
    throwSessionExpired(message);
  }

  return parsed.replay;
}

export async function checkAppSession() {
  try {
    const response = await apiAppFetch('getUserDetails', {
      method: 'POST',
      body: JSON.stringify({}),
    });

    if (response.status === 401) {
      let message = 'Session expired. Please login again.';
      try {
        const data = await response.json();
        if (data && data.message) {
          message = String(data.message);
        }
      } catch (e) {
        // use default message
      }
      throwSessionExpired(message);
    }

    return true;
  } catch (error) {
    if (error && error.sessionExpired) {
      return false;
    }
    return true;
  }
}

function buildFetchOptions(options, token) {
  const headers = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return {
    ...options,
    method: options.method || 'POST',
    cache: 'no-store',
    headers,
  };
}

export async function apiAppFetch(endpoint, options = {}) {
  const token = await getToken();
  const response = await fetch(
    `${config.API_APP_BASE_URL}${endpoint}`,
    buildFetchOptions(options, token)
  );
  return interceptSessionExpired(response, endpoint);
}

export async function mediatorAppFetch(options = {}) {
  const token = await getToken();
  const response = await fetch(
    config.MEDIATOR_APP_URL,
    buildFetchOptions(options, token)
  );
  return interceptSessionExpired(response, 'mediator');
}
